/**
 * A voice pack is a folder of recordings under `src/client/assets/audio/dialogue/hero voices/`. Its clips are named after the event they
 * answer (see `voice-events.ts`), numbered for variety: `attack 1.mp3`, `attack 2.mp3`...
 */
export interface VoicePackDefinition {
  id: string;
  name: string;
  /** The folder holding the clips. */
  folder: string;
  /** Rig families this pack suits. A hero who names no pack takes the first pack listing their body, else the first pack. */
  bodies: string[];
}

export const voicePacks: VoicePackDefinition[] = [
  { id: 'female-calm', name: 'Female · Calm', folder: 'female calm', bodies: ['human-female'] },
  { id: 'male-calm', name: 'Male · Calm', folder: 'male calm', bodies: ['human-male'] },
  { id: 'female-expressive', name: 'Female · Expressive', folder: 'female expressive', bodies: ['human-female'] },
  { id: 'male-sage', name: 'Male · Sage', folder: 'male sage', bodies: ['human-male'] },
];

/** The pack a body gets unless the player picks another: the first that suits it, else the first pack. While the library is small this gives every hero a voice. */
export function defaultVoicePack(rig: string | undefined, packs: { id: string; bodies: string[] }[] = voicePacks): string | null {
  return (rig ? packs.find(pack => pack.bodies.includes(rig)) : undefined)?.id ?? packs[0]?.id ?? null;
}

/** The pack a hero speaks with: the one they name (a pack that no longer exists falls back), else the default for their body. Null only when there are no packs. */
export function voicePackFor(hero: { voice?: string; appearance?: { rig?: string } }, packs: VoicePackDefinition[] = voicePacks): string | null {
  return hero.voice && packs.some(pack => pack.id === hero.voice) ? hero.voice : defaultVoicePack(hero.appearance?.rig, packs);
}

/**
 * Monsters that cast speak with a recorded incantation too, in a folder per body under `src/client/assets/audio/dialogue/bandit voices/`,
 * laid out like a hero pack's `casting/` folder (`<school>.mp3`). A caster of any other body stays silent.
 */
// Bandits come in every ancestry they are drawn from (`encounters/bandits.ts`); each sex shares one recording.
const banditVoiceFolders: Readonly<Record<string, string>> = Object.fromEntries(['human', 'orc', 'dwarf', 'elf'].flatMap(ancestry =>
  [[`${ancestry}-female`, 'bandit voices/female'], [`${ancestry}-male`, 'bandit voices/male']]));
export const monsterCastingFolder = (rig: string | undefined): string | null => (rig ? banditVoiceFolders[rig] : undefined) ?? null;
